import {
  Card,
  CardBody,
  Carousel,
  COLOR,
  getColorClassName,
  Heading,
  Image,
  Paragraph,
  Separator,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { IMAGE_SIZE } from "./offerHelperComponents"
import { OfferNavButtons, OfferSaveButton } from "./OfferNavButtons"
import { useOfferSettings } from "./OfferSettingsContext"
import { OFFER_STEPS } from "./OfferStepper"

const IMAGE_URL_CLOCK = "./assets/clock.png"
const IMAGE_URL_FOLDER = "./assets/folder.png"
const IMAGE_URL_DOCUMENT = "./assets/document.png"

interface ICarouselData {
  id: string
  title: string
  text: string
  imgUrl: string
}

const CAROUSEL_DATA: ICarouselData[] = [
  {
    id: "c1",
    title: "offers.welcome.card1.title",
    text: "offers.welcome.card1.text",
    imgUrl: IMAGE_URL_CLOCK,
  },
  {
    id: "c2",
    title: "offers.welcome.card2.title",
    text: "offers.welcome.card2.text",
    imgUrl: IMAGE_URL_FOLDER,
  },
  {
    id: "c3",
    title: "offers.welcome.card3.title",
    text: "offers.welcome.card3.text",
    imgUrl: IMAGE_URL_DOCUMENT,
  },
]

interface IOfferCarouselChildCardProps {
  title: string
  text: string
  imgUrl: string
}

const OfferCarouselChildCard: React.FC<IOfferCarouselChildCardProps> = ({
  title,
  text,
  imgUrl,
}) => {
  const content = useMatchMediaTierProp({
    xs: (
      <>
        <Heading
          level={1}
          className={`mb-3 ${getColorClassName(COLOR.WHITE, { foreground: true })}`}
        >
          {title}
        </Heading>

        <Paragraph
          className={getColorClassName(COLOR.WHITE, { foreground: true })}
        >
          {text}
        </Paragraph>
      </>
    ),
    md: (
      <>
        <Heading level={1} className={`mb-3`}>
          {title}
        </Heading>

        <Paragraph>{text}</Paragraph>
      </>
    ),
  })
  return (
    <div className="text-center">
      <Image
        src={imgUrl}
        height={IMAGE_SIZE}
        width={IMAGE_SIZE}
        alt=""
        className="mb-3 mt-3"
      />

      {content}
    </div>
  )
}

const OfferWelcomePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currIdx, setCurrIdx] = useState(0)
  const { setIsCloseButtonVisible } = useOfferSettings()
  const lastIdx = CAROUSEL_DATA?.length > 0 ? CAROUSEL_DATA.length - 1 : 0

  useEffect(() => {
    setIsCloseButtonVisible(currIdx === lastIdx)
  }, [currIdx, lastIdx, setIsCloseButtonVisible])

  const carouselContent = (
    <Carousel
      showStepper
      isFluidOnXS={false}
      veilLeftWidth={50}
      veilRightWidth={50}
      visibleItems={1}
      currentItemIndex={currIdx}
      onNext={() => setCurrIdx(currIdx + 1)}
      onPrevious={() => setCurrIdx(currIdx - 1)}
      buttonNextLabel=""
      buttonPreviousLabel=""
    >
      {CAROUSEL_DATA?.map((item) => (
        <OfferCarouselChildCard
          key={item.id}
          title={t(item.title)}
          text={t(item.text)}
          imgUrl={item.imgUrl}
        />
      ))}
    </Carousel>
  )

  const nextButtonHandler = () => {
    if (currIdx < lastIdx) {
      setCurrIdx(currIdx + 1)
    } else {
      skipButtonHandler()
    }
  }

  const skipButtonHandler = () => {
    setIsCloseButtonVisible(true)
    navigate(`/offer/${OFFER_STEPS.Calculator}`, { replace: true })
  }

  const navButtonsContent = useMatchMediaTierProp({
    xs: (
      <OfferNavButtons
        onNextButtonClicked={nextButtonHandler}
        isAlternativeColored={true}
      />
    ),
    md: <OfferNavButtons onNextButtonClicked={nextButtonHandler} />,
  })

  const saveButtonHandler = () => {
    setCurrIdx(lastIdx)
  }

  const saveButtonContent = useMatchMediaTierProp({
    xs: (
      <>
        {currIdx < lastIdx && (
          <OfferSaveButton
            saveButtonText={t("offers.button.skip")}
            onSaveButtonClicked={saveButtonHandler}
            skipModal
            isAlternativeColored={true}
          />
        )}
      </>
    ),
    md: (
      <>
        {currIdx < lastIdx && (
          <OfferSaveButton
            saveButtonText={t("offers.button.skip")}
            onSaveButtonClicked={saveButtonHandler}
            skipModal
          />
        )}
      </>
    ),
  })

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {carouselContent}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>{carouselContent}</CardBody>
        <Separator />
        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <>
      {pageContent}

      {saveButtonContent}
    </>
  )
}

export default OfferWelcomePage
